import Enumerable from "linq";
import moment from "moment";

import { IPatientBean } from "../../../patient/ver2/beans";
import { IHisInformationBean, IHisInformationSetBean } from "../../../his/ver2/beans";

import { IReceptionSearchResultBean, IReceptionDetailSearchResultBean, IReceptionDetailStatusSearchConditionBean, IWorkflowStatusSearchConditionBean, IReceptionDetailAllSearchConditionBean, IReceptionDetailPresetSortConditionBean, IReceptionDetailPresetSortConditionItemBean, IReceptionDetailCsvFilePrintBean
} from "../beans/search";
import { IReceptionListDisplayBean, IReceptionSummaryBean, IReceptionDetailListDisplayBean, IReceptionDetailSummaryBean, IReceptionTodoListDisplayBean
} from "../beans/list";
import { IWorkflowDocumentSaveResultBean, IWorkflowDocumentSupplementBean, IWorkflowDocumentViewBean, IWorkflowHisInformationBean, IWorkflowDocumentPermissionsBean, IWorkflowDocumentInfoBean, IWorkflowAutoRegisterResultBean 
} from "../beans/document";
import { IReceptionReferenceBean, IReceptionReferenceDisplayBean, IWorkflowReferenceBean, IWorkflowPhaseReferenceBean, IReceptionPermissionsBean, IReceptionDetailPermissionsBean 
} from "../beans/reference";
import {
  IReceptionReportTypeBean,
  IWorkflowOperationBean,
  IReceptionOperationBean,
  IPatientAddressBean,
  IReceptionInputSectionBean,
  IReceptionDeliveryKindBean,
  IReceptionDetailListItemBean,
  IReceptionDetailListColorBean,
  IProgressConditionKindsBean,
  IManagerReferenceBean,
  IReceptionListCardStyleDispInfoBean,
} from "../beans/items";
import { IReceptionCreateBean, IReceptionEditorBean, IReceptionEditorDisplayBean, IReceptionEditorInputUnitDisplayBean, IWorkflowRouteEditorBean, IWorkflowPhaseEditorBean, IReceptionEditorPermissionsBean, IReceptionEditorInstantCloserSettingBean 
} from "../beans/editor";
import { IReceptionPatientInfoBean, IReceptionBean, IReceptionDetailBean, IReceptionApplicantBean, IReceptionRecipientBean, IReceptionDeliveryBean, IReceptionContactBean, IReceptionContactDetailBean, IReceptionManagerBean, IReceptionProofPeriodBean, IReceptionCommentSetBean, IReceptionCommentBean 
} from "../beans/reception";

import { WorkflowDocument, WorkflowDocumentViewer, WorkflowDocumentSaveResult, WorkflowDocumentPermissions, WorkflowHisInformation, WorkflowDocumentInfo, WorkflowAutoRegisterResult
} from "../../../../domain/workflow/document";
import {
  ReceptionInputSection,
  WorkflowOperation,
  ReceptionReportType,
  ReceptionOperation,
  ReceptionDeliveryKind,
  ReceptionDetailListItem,
  ReceptionDetailListColor,
  ManagerReference,
  ReceptionListCardStyleDispInfo,
} from "../../../../domain/workflow/items";
import { ReceptionListDispInfo, ReceptionSummary, ReceptionDetailListDispInfo, ReceptionDetailSummary, ReceptionTodoListDispInfo 
} from "../../../../domain/workflow/list";
import { ReceptionSearchResult, ReceptionDetailSearchResult, ReceptionDetailPresetSearchCondition, ReceptionDetailStatusSearchCondition, WorkflowStatusSearchCondition, ReceptionDetailSearchCondition, ReceptionDetailPresetSortCondition, ReceptionDetailPresetSortConditionItem, ReceptionSearchActiveKinds, ReceptionDetailCsvFilePrint
} from "../../../../domain/workflow/search";
import { ReceptionEditor, ReceptionEditorDispInfo, ReceptionEditorInputUnitDispInfo, WorkflowRouteEditorModel, WorkflowPhaseEditorModel, ReceptionEditorPermissions, ReceptionEditorInstantCloserSetting 
} from "../../../../domain/workflow/editor";
import { ReceptionPatient, Reception, ReceptionDetail, ReceptionApplicant, ReceptionRecipient, ReceptionDelivery, ReceptionContact, ReceptionContactDetail, ReceptionManager, ReceptionProofPeriod, ReceptionCommentSet, ReceptionComment 
} from "../../../../domain/workflow/reception";
import { ReceptionReference, ReceptionReferenceDispInfo, WorkflowReference, WorkflowPhaseReference, ReceptionDetailPermissions, ReceptionPermissions 
} from "../../../../domain/workflow/reference";

import { ProgressConditionKinds, ReceptionEditKinds } from "../../../../domain/workflow/kinds";

import { CurrentEditors, Document, DocumentTokenInformation, UnmanagedDocumentSummaryInformation } from "../../../../domain/document";
import { DateDuration } from "../../../../domain/system";
import { ReceptionBeanBaseTranslator } from "./ReceptionBeanBaseTranslator";
import { ICurrentEditorBean, IDocumentTokenInformationBean, IUnmanagedDocumentSummaryInformationBean } from "../../../../infrastructure/document/ver2/beans";
import { IReceptionDetailPresetSearchConditionBean } from "../beans/search/IReceptionDetailPresetSearchConditionBean";
import { IReceptionDetailPresetSearchConditionOrderBean } from "../beans/search/IReceptionDetailPresetSearchConditionOrderBean";
import { IUserBean } from "../../../../infrastructure/user/ver2/beans";
import { User } from "../../../../domain/user";
import { HisInformationItemSet } from "../../../../domain/his";
import { ReceptionSearchActiveKindTranslator } from "./ReceptionSearchActiveKindTranslator";  // 循環参照になってしまったので個別でimportしている

// 診断書WF用bean<-->model 変換クラス
export class ReceptionBeanTranslator extends ReceptionBeanBaseTranslator {
  constructor(_getToken: () => string) { super(_getToken); }

  // ------bean -> model -------------------------------

  // 依頼一覧画面用表示設定
  public toReceptionListDispInfo(bean: IReceptionListDisplayBean): ReceptionListDispInfo {
    const model = new ReceptionListDispInfo();
    this.setReceptionListDispInfoBase(bean, model);

    model.deliveryKindList = this.toReceptionDeliveryKinds(bean.deliveryKindList);

    return model;
  }

  // 依頼受付検索結果用モデル
  public toReceptionSearchResult(bean: IReceptionSearchResultBean): ReceptionSearchResult {
    const model = new ReceptionSearchResult();
    model.list = this.toReceptionSummaryList(bean.list);
    model.overLimitMessage = bean.overLimitMessage;
    
    return model;
  }

  private toReceptionSummaryList(beans: IReceptionSummaryBean[]): ReceptionSummary[] {
    return Enumerable.from(beans)
      .select(bean => this.toReceptionSummary(bean))
      .toArray();
  }

  private toReceptionSummary(bean: IReceptionSummaryBean): ReceptionSummary {
    const model = new ReceptionSummary();
    model.id = bean.id;
    model.status = bean.status;
    model.receptionNumber = bean.receptionNumber;
    model.receptionDate = this._dateTranslator.fromDateString(bean.receptionDate);
    model.patient = this.toPatient(bean.patient);
    model.isTemporary = bean.isTemporary;
    model.deleted = bean.deleted;
    model.hasDeletedDetail = bean.hasDeletedDetail;
    model.permissions = this.toReceptionPermissions(bean.permissions);

    Object.freeze(model); // これでメモリ減る？

    return model;
  }

  // 依頼詳細一覧画面用表示設定
  public toReceptionDetailListDispInfo(bean: IReceptionDetailListDisplayBean): ReceptionDetailListDispInfo {
    const model = new ReceptionDetailListDispInfo();
    this.setReceptionListDispInfoBase(bean, model);

    model.isSuspendStatusEnable = bean.isSuspendStatusEnable;
    model.isRequestAnotherEnable = bean.isRequestAnotherEnable;

    model.workflowStatuses = this.toWorkflowStatusNames(bean.workflowStatuses);
    model.defaultSelectedWorkflowStatusCodeList = bean.defaultSelectedWorkflowStatusCodeList;
    model.reportTypes = this.toReceptionReportTypes(bean.reportTypes);
    model.defaultSearchPeriod = this.toDateDuration(bean.defaultSearchPeriod);
    model.deliveryKindList = this.toReceptionDeliveryKinds(bean.deliveryKindList);
    model.displayItemList = this.toDisplayItemList(bean.displayItemList);
    model.workflowListDoubleClickAction = bean.workflowListDoubleClickAction;
    model.displayColor = this.toDisplayListColor(bean.displayColor);
    model.inputSectionList = Enumerable.from(bean.inputSectionList)
      .select(item => new ReceptionInputSection(item.code, item.name, item.isDisable))
      .toArray();
    model.cardStyle = this.toReceptionListCardStyleDispInfo(bean.cardStyle);

    return model;
  } 

  // 依頼詳細検索結果用モデル
  public toReceptionDetailSearchResult(bean: IReceptionDetailSearchResultBean): ReceptionDetailSearchResult {
    const model = new ReceptionDetailSearchResult();
    model.list = this.toReceptionDetailSummaryList(bean.list);
    model.overLimitMessage = bean.overLimitMessage;
    
    return model;
  }

  // 依頼詳細一覧用モデル
  public toReceptionDetailSummaryList(beans: IReceptionDetailSummaryBean[]): ReceptionDetailSummary[] {
    return Enumerable.from(beans)
      .select(bean => this.toReceptionDetailSummary(bean))
      .toArray();
  }

  private toReceptionDetailSummary(bean: IReceptionDetailSummaryBean): ReceptionDetailSummary {
    const model = new ReceptionDetailSummary();

    model.id = bean.id;
    model.status = this.toWorkflowStatus(bean.status);
    model.format = this.toFormatReference(bean.format);
    model.patient = this.toPatient(bean.patient);
    model.department = this.toDepartment(bean.department);
    model.receptionId = bean.receptionId;
    model.receptionNumber = bean.receptionNumber;
    model.receptionDate = this._dateTranslator.fromDateString(bean.receptionDate);
    model.createLimitDate = this._dateTranslator.fromDateString(bean.createLimitDate);
    model.deliveryLimitDate = this._dateTranslator.fromDateString(bean.deliveryLimitDate);
    model.finishDate = this._dateTranslator.fromDateString(bean.finishDate);
    model.deliveryKind = bean.deliveryKind;
    model.managers = Enumerable.from(bean.managers).select(s => this.toUser(s)).toArray();
    model.generalFlag1 = this.toReceptionFlag(bean.generalFlag1);
    model.isBacked = this.toReceptionFlag(bean.isBacked);
    model.isTemporary = this.toReceptionFlag(bean.isTemporary);
    model.isNearlyExpired = this.toReceptionFlag(bean.isNearlyExpired);
    model.isExpired = this.toReceptionFlag(bean.isExpired);
    model.isPaper = this.toReceptionFlag(bean.isPaper);
    model.isCopy = this.toReceptionFlag(bean.isCopy);
    model.paperName = bean.paperName;
    model.paperCompanyName = bean.paperCompanyName;
    model.document = this.toDocumentIdentifier(bean.patient, bean.documentId);
    model.deleted = bean.deleted;

    model.createDateTime = this._dateTranslator.fromDateTimeString(bean.createDateTime);
    model.createUser = this.toUser(bean.createUser);
    model.inputSection = this.toReceptionInputSection(bean.inputSection);
    model.updateDateTime = this._dateTranslator.fromDateTimeString(bean.updateDateTime);

    model.nextMessages = bean.nextMessages;
    model.generalComments = bean.generalComments;
    model.receiptDate = this._dateTranslator.fromDateString(bean.receiptDate);
    model.inOutType = bean.inOutType;

    Object.freeze(model); // これでメモリ減る？

    return model;
  }

  // Todoリスト画面用表示設定
  public toReceptionTodoListDispInfo(bean: IReceptionTodoListDisplayBean): ReceptionTodoListDispInfo {
    const model = new ReceptionTodoListDispInfo();
    this.setReceptionListDispInfoBase(bean, model);
    
    model.workflowStatuses = this.toWorkflowStatusNames(bean.workflowStatuses);
    model.defaultSelectStatus = this.toWorkflowStatusName(bean.defaultSelectStatus);
    model.reportTypes = this.toReceptionReportTypes(bean.reportTypes);
    model.departments = this.toDepartments(bean.departments);
    model.displayColor = this.toDisplayListColor(bean.displayColor);
    model.myDraftTabEnable = bean.myDraftTabEnable;
    model.myWrittenTabEnable = bean.myWrittenTabEnable;
    model.isDocumentCreateButtonEnable = bean.isDocumentCreateButtonEnable;
    model.defaultSelectedTodoListTab = bean.defaultSelectedTodoListTab;
    model.cardStyle = this.toReceptionListCardStyleDispInfo(bean.cardStyle);

    return model;
  }

  // 文書種別
  private toReceptionReportType(bean: IReceptionReportTypeBean): ReceptionReportType {
    if (!bean) { return null; }
    return new ReceptionReportType(bean.id, bean.name);
  }

  private toReceptionReportTypes(beans: IReceptionReportTypeBean[]): ReceptionReportType[] {
    return Enumerable.from(beans)
      .select(bean => this.toReceptionReportType(bean))
      .toArray();
  }

  // 交付方法
  private toReceptionDeliveryKind(bean: IReceptionDeliveryKindBean): ReceptionDeliveryKind {
    if (!bean) { return null; }
    const model = new ReceptionDeliveryKind();
    model.code = bean.code;
    model.name = bean.name;
    model.isEnable = bean.isEnable;
    return model;
  }

  private toReceptionDeliveryKinds(beans: IReceptionDeliveryKindBean[]): ReceptionDeliveryKind[] {
    return Enumerable.from(beans)
      .select(bean => this.toReceptionDeliveryKind(bean))
      .toArray();
  }

  private toDisplayProgressConditionKinds(bean: IProgressConditionKindsBean): ProgressConditionKinds[] {
    if (!bean) { return []; }

    const kinds = [] as ProgressConditionKinds[];
    if (bean.noneEnable) { kinds.push(ProgressConditionKinds.None); }
    if (bean.anyEnable) { kinds.push(ProgressConditionKinds.Any); }
    if (bean.allEnable) { kinds.push(ProgressConditionKinds.All); }
    // 未実装なので、実装されたら追加する
    // if (bean.allOrderedEnable) { kinds.push(ProgressConditionKinds.AllOrdered); }
    return kinds;
  }

  private toDefaultProgressCondition(bean: IProgressConditionKindsBean): ProgressConditionKinds {
    if (!bean) { return ProgressConditionKinds.None; }

    const defaultProgressCondition = bean.defaultProgressCondition;
    const progressConditionKinds = this.toDisplayProgressConditionKinds(bean);

    if (progressConditionKinds.length === 0) {
      return ProgressConditionKinds.None;
    } else if (!defaultProgressCondition || !progressConditionKinds.includes(defaultProgressCondition)) {
      return progressConditionKinds[0];
    } else {
      return defaultProgressCondition;
    }
  }

  private toReceptionDetailItem(bean: IReceptionDetailListItemBean): ReceptionDetailListItem {
    if (!bean) { return null; }
    const model = new ReceptionDetailListItem();
    model.columnName = bean.columnName;
    model.displayName = bean.displayName;
    model.index = bean.index;
    model.isEnable = bean.isEnable;
    return model;
  }

  public toDisplayItemList(beans: IReceptionDetailListItemBean[]): ReceptionDetailListItem[] {
    return Enumerable.from(beans)
      .select(bean => this.toReceptionDetailItem(bean))
      .toArray();
  }

  private toDisplayListColor(beans: IReceptionDetailListColorBean): ReceptionDetailListColor {
    return new ReceptionDetailListColor(
      beans.expiredBackgroundColor,
      beans.nearlyExpiredBackgroundColor,
      beans.expiredForegroundColor,
      beans.nearlyExpiredForegroundColor,
      beans.backedForegroundColor,
      beans.temporaryForegroundColor
    );
  }

  // 依頼受付新規画面用モデル
  public toReceptionNewEditor(bean: IReceptionCreateBean): ReceptionEditor {
    const model = new ReceptionEditor();
    model.displayInfo = this.toReceptionEditorDispInfo(bean.displaySetting);
    // detailsは1つだけ追加した状態でNewされる
    model.receptionDetails[0].proofPeriods = this.createEmptyProofPeriods(model.displayInfo.proofPeriodDefaultCount);
    model.requestSheetFormats = this.toFormatReferences(bean.requestSheetFormats);
    this.setDefaultInputSection(model, bean.displaySetting.defaultInputSectionCode);
    this.setDefaultProgressCondition(model.receptionDetails[0], model.displayInfo.defaultProgressCondition);
    this.setDefaultDeliveryType(model, bean.displaySetting.defaultDeliveryTypeCode);

    return model;
  }

  // 入力部署初期値
  private setDefaultInputSection(model: ReceptionEditor, defaultInputSectionCode: number): void {
    if (defaultInputSectionCode == null || !model.displayInfo.inputSectionList) { return; }
    const found = model.displayInfo.inputSectionList.find(s => s.id === defaultInputSectionCode);
    if (found) {
      model.reception.inputSection = found;
    }
  }

  // 交付方法初期値
  private setDefaultDeliveryType(model: ReceptionEditor, defaultDeliveryTypeCode: number): void {
    if (defaultDeliveryTypeCode == null || !model.displayInfo.deliveryKindList) { return; }
    // 患者選択したら結局はクリアされてしまう・・
    // ここでのセットは、最初に表示したときの見た目を整える意味しかない
    const found = model.displayInfo.deliveryKindList.find(s => s.code === defaultDeliveryTypeCode && s.isEnable);
    if (found) {
      model.reception.delivery.type = found.clone();
    }
  }

  // 証明期間（空枠追加）
  private createEmptyProofPeriods(defaultCount: number): ReceptionProofPeriod[] {
    const array: ReceptionProofPeriod[] = [];
    this.fillProofPeriods(array, defaultCount);
    return array;
  }

  private setDefaultProgressCondition(model: ReceptionDetail, defaultProgressCondition: number): void {
    if (model && model.route) {
      model.route.progressCondition = defaultProgressCondition;
    }
  }

  // 依頼受付編集用モデル
  public toReceptionEditor(bean: IReceptionEditorBean, isQuote: boolean): ReceptionEditor {
    const model = new ReceptionEditor();
    if (bean.reception.isTemporary) {
      model.mode = ReceptionEditKinds.TempEdit;
    } else if (bean.reception.id) {
      model.mode = ReceptionEditKinds.Edit;
    }

    console.log(model.mode);

    model.displayInfo = this.toReceptionEditorDispInfo(bean.displaySetting);
    model.reception = this.toReception(bean.reception, bean.patientAddress, bean.hisInformation);
    model.receptionDetails = this.toReceptionDetails(bean.receptionDetails, bean.reception.patient, model.displayInfo.proofPeriodDefaultCount, isQuote);
    model.requestSheetFormats = this.toFormatReferences(bean.requestSheetFormats);
    model.sourceReceptionId = bean.sourceReceptionId;
    model.sourceReceptionDetailId = bean.sourceReceptionDetailId;
    model.lastWorkflowHistoryId = bean.lastWorkflowHistoryId;
    // 単一入力設定されてる場合は1件目の内容を共通へ格納する.(依頼編集時はバックエンドから設定無効で来るのでフロントでは考慮不要.)
    const firstDetail = model.receptionDetails[0];
    if (model.displayInfo.inputUnitSetting.isCommentInputSingle) {
      model.commonReceptionDetail.comment = firstDetail.comment.clone();
    }
    if (model.displayInfo.inputUnitSetting.isNextMessageInputSingle) {
      model.commonReceptionDetail.nextMessage = firstDetail.nextMessage.clone();
      model.commonReceptionDetail.commentSet = firstDetail.commentSet.clone();
    }
    if (model.displayInfo.inputUnitSetting.isDepartmentInputSingle && firstDetail.department !== null) {
      model.commonReceptionDetail.department = firstDetail.department.clone();
    }
    if (model.displayInfo.inputUnitSetting.isManagerInputSingle && firstDetail.managers !== null) {
      model.commonReceptionDetail.managers = Enumerable.from(firstDetail.managers).select(m => m.clone()).toArray();
      model.commonReceptionDetail.route.progressCondition = firstDetail.route.progressCondition;
    }

    if (model.displayInfo.inputUnitSetting.isPhaseInputSingle && firstDetail.route.phases != null) {
      model.commonReceptionDetail.route.phases = Enumerable.from(firstDetail.route.phases).select(p => p.clone()).toArray();
    }
    model.commonReceptionDetail.sourceReceptionDetailId = firstDetail.sourceReceptionDetailId;
    model.commonReceptionDetail.quoted = firstDetail.quoted;
    
    return model;
  }

  // 編集画面用表示設定
  private toReceptionEditorDispInfo(bean: IReceptionEditorDisplayBean): ReceptionEditorDispInfo {
    const model = new ReceptionEditorDispInfo();

    if (bean.relationshipList) {
      model.relationshipList = this.toReceptionTypeItems(bean.relationshipList);
    }
    if (bean.paperNameList) {
      model.paperNameList = this.toReceptionTypeItems(bean.paperNameList);
    }
    if (bean.inputSectionList) {
      model.inputSectionList = Enumerable.from(bean.inputSectionList)
        .select(item => new ReceptionInputSection(item.code, item.name, item.isDisable))
        .toArray();
    }
    model.labelSetting = this.toReceptionDispInfo(bean.labelSetting);
    model.isManagerRequired = bean.isManagerRequired;
    model.isHospitalizationHistoryEnable = bean.isHospitalizationHistoryEnable;
    model.isVisitHistoryEnable = bean.isVisitHistoryEnable;
    model.permissions = this.toReceptionEditorPermissions(bean.permissions);
    model.isRestrictCommentEnable = bean.isRestrictCommentEnable;
    model.isCommentEditable = bean.isCommentEditable;
    model.isRestrictCommentEditable = bean.isRestrictCommentEditable;
    model.isNextMessageEditable = bean.isNextMessageEditable;
    model.proofPeriodDefaultCount = bean.proofPeriodDefaultCount > 0 ? bean.proofPeriodDefaultCount : 3; // 設定値が不正な場合はシステム初期値の3個
    model.proofPeriodMaxCount = bean.proofPeriodMaxCount;
    model.inputUnitSetting = this.toReceptionEditorInputUnitDispInfo(bean.inputUnitSetting);
    model.deliveryKindList = this.toReceptionDeliveryKinds(bean.deliveryKindList);
    model.displayProgressConditions = this.toDisplayProgressConditionKinds(bean.progressConditionSetting);
    model.defaultProgressCondition = this.toDefaultProgressCondition(bean.progressConditionSetting);
    model.receptionDetailMaxCount = bean.receptionDetailMaxCount;
    model.isPublishReceipt = bean.isPublishReceiptEnable;
    model.receiptFormatOriginalIds = bean.receiptFormatOriginalIds ? bean.receiptFormatOriginalIds : [];
    model.ReceiptDateDefaultDays = bean.receiptDateDefaultDays;
    model.defaultContactRequired = bean.defaultContactRequired;
    model.defaultDeliveryTypeCode = bean.defaultDeliveryTypeCode;
    model.shouldAutoApplyProjectTeamInfo = bean.shouldAutoApplyProjectTeamInfo;
    model.isDepartmentRequired = bean.isDepartmentRequired;
    model.shouldSelectManagers = bean.shouldSelectManagers;
    model.nondisplayDepartmentCodeList = bean.nondisplayDepartmentCodeList;
    model.isFormatSelectorAllTabEnable = bean.isFormatSelectorAllTabEnable;
    model.instantCloserSetting = this.toInstantCloserSetting(bean.instantCloserSetting);
    model.isPaperEnable = bean.isPaperEnable;
    model.isPaperNameEnable = bean.isPaperNameEnable;
    model.isPaperCompanyNameEnable = bean.isPaperCompanyNameEnable;
    model.commentMode = bean.commentMode;
    model.nextMessageMode = bean.nextMessageMode;
    model.restrictCommentMode = bean.restrictCommentMode;
    model.commentDefaultSortType = bean.commentDefaultSortType;
    model.isCommentRegisterable = bean.isCommentRegisterable;
    model.isNextMessageRegisterable = bean.isNextMessageRegisterable;
    model.isRestrictCommentRegisterable = bean.isRestrictCommentRegisterable;
    model.isWorkflowPhasesQuoteEnable = bean.isWorkflowPhasesQuoteEnable;
    model.isCommentEditableSingleUnitOnly = bean.isCommentEditableSingleUnitOnly;
    model.shouldSortCommentAsc = bean.shouldSortCommentAsc;
    if (bean.contactNameList) {
      model.contactNameList = this.toReceptionTypeItems(bean.contactNameList);
    }
    if (bean.paymentStatusList) {
      model.paymentStatusList = this.toReceptionTypeItems(bean.paymentStatusList);
    }
    model.shouldShowManagerPhoneNumber = bean.shouldShowManagerPhoneNumber;

    return model;
  }

  //  依頼入力画面用入力単位表示設定
  private toReceptionEditorInputUnitDispInfo(bean: IReceptionEditorInputUnitDisplayBean): ReceptionEditorInputUnitDispInfo {
    const model = new ReceptionEditorInputUnitDispInfo();

    model.isDepartmentInputSingle = bean.isDepartmentInputSingle;
    model.isManagerInputSingle = bean.isManagerInputSingle;
    model.isPhaseInputSingle = bean.isPhaseInputSingle;
    model.isCommentInputSingle = bean.isCommentInputSingle;
    model.isNextMessageInputSingle = bean.isNextMessageInputSingle;

    return model;
  }

  private toInstantCloserSetting(bean: IReceptionEditorInstantCloserSettingBean): ReceptionEditorInstantCloserSetting {
    const model = new ReceptionEditorInstantCloserSetting();

    model.isEnable = bean.isEnable;
    model.waitTimeAsMilliSeconds = bean.waitTimeAsMilliSeconds;
    model.shouldShowNotification = bean.shouldShowNotification;

    return model;
  }

  // 依頼患者情報
  public toReceptionPatient(patientBean: IPatientBean, bean: IReceptionPatientInfoBean): ReceptionPatient {
    const model = new ReceptionPatient();

    model.patient = this.toPatient(patientBean);
    model.patientNote = bean.patientNote;
    model.address = this.toPatientAddress(bean.address);
    model.hisInformation = this.toHisInformation(bean.hisInformation);

    return model;
  }


  // 依頼参照用モデル
  public toReceptionReference(bean: IReceptionReferenceBean): ReceptionReference {
    const model = new ReceptionReference();

    model.reception = this.toReception(bean.reception);
    model.receptionDetail = this.toReceptionDetail(bean.receptionDetail, bean.reception.patient, 0, false);
    model.requestSheetFormats = this.toFormatReferences(bean.requestSheetFormats);
    model.requestSheetFormatStatus = bean.requestSheetFormatStatus;
    model.requestSheetDocument = this.toDocumentSummary(bean.requestSheetDocument);
    model.requestSheetReferenceFiles = this.toReferenceFileInformations(bean.requestSheetReferenceFiles);
    model.document = this.toDocumentSummary(bean.document);
    model.documentReferenceFiles = this.toReferenceFileInformations(bean.documentReferenceFiles);
    model.displayInfo = this.toReceptionReferenceDispInfo(bean.displaySetting);
    model.workflow = this.toWorkflowReference(bean.workflow);

    const documentId = model.document ? model.document.id : null;
    model.operationHistories = this.toReceptionOperations(documentId, bean.operationHistories);
    model.managersHistories = this.toManagerReferences(bean.managersHistories);
    model.quoteSourceDocument = this.toDocumentSummary(bean.quoteSourceDocument);
    model.receiptFormatOriginalIds = bean.receiptFormatOriginalIds ? bean.receiptFormatOriginalIds : [];

    return model;
  }

  // 参照画面用表示設定
  private toReceptionReferenceDispInfo(bean: IReceptionReferenceDisplayBean): ReceptionReferenceDispInfo {
    const model = new ReceptionReferenceDispInfo();
    model.labelSetting = this.toReceptionDispInfo(bean.labelSetting);
    model.permissions = this.toReceptionDetailPermissions(bean.permissions);

    return model;
  }

  // ワークフロー参照
  private toWorkflowReference(bean: IWorkflowReferenceBean): WorkflowReference {
    if (!bean) { return null; }

    const model = new WorkflowReference();
    model.id = bean.id;
    model.phases = this.toWorkflowPhaseReferences(bean.phases);
    model.status = this.toWorkflowStatusName(bean.status);
    model.createLimitDate = this._dateTranslator.fromDateString(bean.createLimitDate);
    model.deliveryLimitDate = this._dateTranslator.fromDateString(bean.deliveryLimitDate);
    model.finishDate = this._dateTranslator.fromDateString(bean.finishDate);
    model.isBacked = bean.isBacked;
    model.isExpired = bean.isExpired;
    model.lastHistoryId = bean.lastHistoryId;

    return model;
  }

  // ワークフローフェーズ
  private toWorkflowPhaseReference(bean: IWorkflowPhaseReferenceBean): WorkflowPhaseReference {
    if (!bean) { return null; }

    const model = new WorkflowPhaseReference();
    model.id = bean.id;
    model.correspondStatus = this.toWorkflowStatusName(bean.correspondStatus);
    model.limitDate = this._dateTranslator.fromDateString(bean.limitDate);
    model.isCurrentPhase = bean.isCurrentPhase;
    model.operations = this.toWorkflowOperations(bean.operations);

    return model;
  }

  private toWorkflowPhaseReferences(beans: IWorkflowPhaseReferenceBean[]): WorkflowPhaseReference[] {
    return Enumerable.from(beans)
      .select(bean => this.toWorkflowPhaseReference(bean))
      .toArray();
  }

  // WF実施情報
  private toWorkflowOperation(bean: IWorkflowOperationBean): WorkflowOperation {
    if (!bean) { return null; }

    const model = new WorkflowOperation();
    model.operator = this.toWorkflowOperator(bean.operator);
    model.operationName = bean.operationName;
    model.operationDateTime = this._dateTranslator.fromDateTimeString(bean.operationDateTime);
    model.nextMessage = bean.nextMessage;
    model.destination = this.toWorkflowDestination(bean.destination);

    return model;
  }

  private toWorkflowOperations(beans: IWorkflowOperationBean[]): WorkflowOperation[] {
    return Enumerable.from(beans)
      .select(bean => this.toWorkflowOperation(bean))
      .toArray();
  }

  // 依頼実施履歴
  private toReceptionOperations(documentId: number, beans: IReceptionOperationBean[]): ReceptionOperation[] {
    return Enumerable.from(beans).select(bean => this.toReceptionOperation(documentId, bean)).toArray();
  }

  private toReceptionOperation(documentId: number, bean: IReceptionOperationBean): ReceptionOperation {
    if (!bean) { return null; }

    const model = new ReceptionOperation();
    model.correspondStatus = this.toWorkflowStatusName(bean.correspondStatus);
    model.operation = this.toWorkflowOperation(bean.operation);
    model.commentContent = bean.commentContent;
    model.documentArchive = this.toDocumentSummaryHistory(documentId, bean.documentArchive);

    return model;
  }

  // 対応責任者履歴
  private toManagerReferences(beans: IManagerReferenceBean[]): ManagerReference[] {
    return Enumerable.from(beans).select(bean => this.toManagerReference(bean)).toArray();
  }

  private toManagerReference(bean: IManagerReferenceBean): ManagerReference {
    if (!bean) { return null; }

    const model = new ManagerReference();
    model.operation = this.toWorkflowOperation(bean.operation);
    model.managers = this.toReceptionManagers(bean.managers);

    return model;
  }

  public toReceptionDetailPresetSearchConditions(beans: IReceptionDetailPresetSearchConditionBean[]): ReceptionDetailPresetSearchCondition[] {
    return Enumerable.from(beans).select(bean => this.toReceptionDetailPresetSearchCondition(bean)).toArray();
  }

  private toReceptionDetailPresetSearchCondition(bean: IReceptionDetailPresetSearchConditionBean): ReceptionDetailPresetSearchCondition {
    if (!bean) { return null; }

    const model = new ReceptionDetailPresetSearchCondition();
    model.key = bean.key;
    model.name = bean.name;
    model.condition = this.toReceptionDetailAllSearchCondition(bean.condition);
    model.presetKind = bean.presetKind;
    model.index = bean.index;
    model.departmentId = bean.departmentId;
    model.isDefaultSelected = bean.isDefaultSelected;
    model.isStatusTabEnabled = bean.isStatusTabEnabled;
    return model;
  }

  private toReceptionDetailAllSearchCondition(bean: IReceptionDetailAllSearchConditionBean): ReceptionDetailSearchCondition {
    if (!bean) { return null; }

    const model = new ReceptionDetailSearchCondition();
    model.dateKind = bean.dateKind;
    model.durationPickerKey = bean.durationPickerKey;
    model.duration = bean.duration ? this.toDateDuration(bean.duration) : new DateDuration(null, null);
    model.limitDate = bean.limitDate ? this.toDateDuration(bean.limitDate) : new DateDuration(null, null);
    model.limitDatePickerKey = bean.limitDatePickerKey;
    // model.patient = ;
    // 現状のフロントエンドは手書きのみの検索UI未実装
    const paperStatus = ReceptionSearchActiveKindTranslator.toReceptionSearchActiveKinds(bean.isPaper);
    model.paperStatus = (paperStatus === ReceptionSearchActiveKinds.Activated)
      ? ReceptionSearchActiveKinds.NotSpecificated
      : paperStatus;
    model.reportType = bean.reportType
      ? new ReceptionReportType(bean.reportType.id, bean.reportType.name)
      : null;
    model.formats = this.toFormatReferences(bean.formats);
    model.paperName = bean.paperName;
    model.managers = this.toUsers(bean.managers);
    model.isManagerMyself = bean.isManagerMyself;
    model.creators = this.toUsers(bean.creators);
    model.isCreatorMyself = bean.isCreatorMyself;
    model.departments = this.toDepartments(bean.departments);
    model.receptionNumber = bean.receptionNumber;
    const deliveryType = new ReceptionDeliveryKind();
    if (bean.deliveryType) {
      deliveryType.code = bean.deliveryType;
    }
    model.deliveryType = deliveryType;
    model.generalFlag1Status = ReceptionSearchActiveKindTranslator.toReceptionSearchActiveKinds(bean.generalFlag1);
    model.statuses = this.toReceptionDetailStatusSearchCondition(bean.statuses);
    model.workflowStatuses = this.toWorkflowStatusSearchConditions(bean.workflowStatuses);
    model.inputSection = this.toReceptionInputSection(bean.inputSection);
    model.inOutType = bean.inOutType;
    model.isPatientIdSpecified = bean.isPatientIdSpecified;
    model.isDepartmentIdSpecified = bean.isDepartmentIdSpecified;
    model.presetPatient = this.toPatient(bean.presetPatient);    
    model.presetDepartments = this.toDepartments(bean.presetDepartments);
    return model;
  }

  public toReceptionDetailPresetSearchConditionKeys(beans: IReceptionDetailPresetSearchConditionOrderBean[]): string[] {
    return Enumerable.from(beans).select(bean => this.toReceptionDetailPresetSearchConditionKey(bean)).toArray();
  }

  private toReceptionDetailPresetSearchConditionKey(bean: IReceptionDetailPresetSearchConditionOrderBean): string {
    return bean ? bean.key : null;
  }

  private toUsers(beans: IUserBean[]): User[] {
    return Enumerable.from(beans).select(bean => this.toUser(bean)).toArray();
  }

  private toReceptionDetailStatusSearchCondition(bean: IReceptionDetailStatusSearchConditionBean): ReceptionDetailStatusSearchCondition {
    if (!bean) { return null; }

    const model = new ReceptionDetailStatusSearchCondition();
    model.isExpired = bean.isExpired;
    model.isTemporary = bean.isTemporary;
    model.isBacked = bean.isBacked;
    return model;
  }

  private toWorkflowStatusSearchConditions(beans: IWorkflowStatusSearchConditionBean[]): WorkflowStatusSearchCondition[] {
    return Enumerable.from(beans).select(bean => this.toWorkflowStatusSearchCondition(bean)).toArray();
  }

  private toWorkflowStatusSearchCondition(bean: IWorkflowStatusSearchConditionBean): WorkflowStatusSearchCondition {
    if (!bean) { return null; }

    const model = new WorkflowStatusSearchCondition();
    model. isSelected = bean.isSelected;
    model.status = this.toWorkflowStatusName(bean.status);
    return model;
  }

  public toReceptionDetailPresetSortConditions(beans: IReceptionDetailPresetSortConditionBean[]): ReceptionDetailPresetSortCondition[] {
    return Enumerable.from(beans).select(bean => this.toReceptionDetailPresetSortCondition(bean)).toArray();
  }

  private toReceptionDetailPresetSortCondition(bean: IReceptionDetailPresetSortConditionBean): ReceptionDetailPresetSortCondition {
    if (!bean) { return null; }

    const model = new ReceptionDetailPresetSortCondition();
    model.label = bean.label;
    model.items = this.toReceptionDetailPresetSortConditionItems(bean.items);
    model.isDefault = bean.isDefault;
    return model;
  }

  private toReceptionDetailPresetSortConditionItems(beans: IReceptionDetailPresetSortConditionItemBean[]): ReceptionDetailPresetSortConditionItem[] {
    return Enumerable.from(beans).select(bean => this.toReceptionDetailPresetSortConditionItem(bean)).toArray();
  }

  private toReceptionDetailPresetSortConditionItem(bean: IReceptionDetailPresetSortConditionItemBean): ReceptionDetailPresetSortConditionItem {
    if (!bean) { return null; }

    const model = new ReceptionDetailPresetSortConditionItem();
    model.sortType = bean.sortType;
    model.orderType = bean.orderType;
    return model;
  }

  public toReceptionDetailCsvFilePrint(bean: IReceptionDetailCsvFilePrintBean, downloadUrl: string): ReceptionDetailCsvFilePrint {
    if (!bean) { return null; }
    const model = new ReceptionDetailCsvFilePrint();
    model.downloadUrl = downloadUrl;
    model.overLimitMessage = bean.overLimitMessage;
    return model;
  }

  // -------------------------

  // 依頼受付
  private toReception(bean: IReceptionBean, address: IPatientAddressBean = null, hisInformation: IHisInformationBean = null): Reception {
    const model = new Reception();
    model.id = bean.id;

    model.patientInfo = new ReceptionPatient();
    model.patientInfo.patient = this.toPatient(bean.patient);
    model.patientInfo.address = this.toPatientAddress(address);
    model.patientInfo.hisInformation = this.toHisInformation(hisInformation);
    model.patientInfo.patientNote = bean.patientNote;

    model.receptionNumber = bean.receptionNumber;
    model.receptionDate = this._dateTranslator.fromDateString(bean.receptionDate);
    model.inputSection = this.toReceptionInputSection(bean.inputSection);
    model.inOutType = bean.inOutType;
    model.document = this.toDocumentIdentifier(bean.patient, bean.documentId);
    model.isTemporary = bean.isTemporary;
    model.isMigrated = bean.isMigrated;
    model.applicant = this.toReceptionApplicant(bean.applicant);
    model.recipient = this.toReceptionRecipient(bean.recipient);
    model.delivery = this.toReceptionDelivery(bean.delivery);
    model.contact = this.toReceptionContact(bean.contact);
    model.deleted = bean.deleted;

    return model;
  }

  // 依頼詳細
  private toReceptionDetails(beans: IReceptionDetailBean[], patient: IPatientBean, proofPeriodDefaultCount: number, isQuote: boolean): ReceptionDetail[] {
    return Enumerable.from(beans)
      .select(bean => this.toReceptionDetail(bean, patient, proofPeriodDefaultCount, isQuote))
      .toArray();
  }

  private toReceptionDetail(bean: IReceptionDetailBean, patient: IPatientBean, proofPeriodDefaultCount: number, isQuote: boolean): ReceptionDetail {
    const model = new ReceptionDetail();
    model.id = bean.id;
    model.format = this.toFormatReference(bean.format);
    model.formatStatus = bean.formatStatus;
    model.isPaper = bean.isPaper;
    model.paperName = bean.paperName;
    model.paperCompanyName = bean.paperCompanyName;
    model.department = this.toDepartment(bean.department);
    model.projectTeam = this.toProjectTeam(bean.projectTeam);	// メンバーは格納されていない
    model.workflowId = bean.workflowId;
    model.document = this.toDocumentIdentifier(patient, bean.documentId);
    model.sourceReceptionDetailId = bean.sourceReceptionDetailId;
    model.sourceDocumentId = bean.sourceDocumentId;
    model.managers = this.toReceptionManagers(bean.managers);
    model.disease = bean.disease;
    model.proofPeriods = this.toReceptionProofPeriods(bean.proofPeriods, proofPeriodDefaultCount);

    model.route = this.toWorkflowRouteEditor(bean.route);
    model.documentFixPhaseStatus = bean.documentFixPhaseStatus;
    model.documentFixPahseProcessedManagers = !bean.documentFixPahseProcessedManagers ? [] : this.toUsers(bean.documentFixPahseProcessedManagers);

    model.comment = this.toReceptionComment(bean.comment);
    model.restrictComment = this.toReceptionComment(bean.restrictComment);
    model.nextMessage = this.toReceptionComment(bean.nextMessage);

    model.commentSet = this.toReceptionCommentSet(bean.commentSet);

    model.deleted = bean.deleted;
    model.quoted = isQuote && !!bean.sourceReceptionDetailId;

    return model;
  }

  // 入力部署
  private toReceptionInputSection(bean: IReceptionInputSectionBean): ReceptionInputSection {
    if (!bean) { return null; }
    return new ReceptionInputSection(bean.code, bean.name, bean.isDisable);
  }

  // 申請者
  private toReceptionApplicant(bean: IReceptionApplicantBean): ReceptionApplicant {
    const model = new ReceptionApplicant();

    model.name = bean.name;
    model.relationship = bean.relationship;
    model.phoneNumber = bean.phoneNumber;
    model.zipCode = bean.zipCode;
    model.address = bean.address;
    model.cellPhone = bean.cellPhone;
    model.email = bean.email;
    model.isPatientAddress = bean.isPatientAddress;

    return model;
  }

  // 受取者
  private toReceptionRecipient(bean: IReceptionRecipientBean): ReceptionRecipient {
    const model = new ReceptionRecipient();
    model.receiptDate = this._dateTranslator.fromDateString(bean.receiptDate);
    model.receiptTime = this._dateTranslator.fromTimeString(bean.receiptTime);
    model.name = bean.name;
    model.relationship = bean.relationship;
    model.paymentStatus = bean.paymentStatus;
    model.receiptMoney = bean.receiptMoney;
    model.note = bean.note;

    return model;
  }

  // 交付先
  private toReceptionDelivery(bean: IReceptionDeliveryBean): ReceptionDelivery {
    const model = new ReceptionDelivery();
    model.type = this.toReceptionDeliveryKind(bean.type);
    model.generalFlag1 = bean.generalFlag1;
    model.zipCode = bean.zipCode;
    model.address = bean.address;
    model.note = bean.note;

    return model;
  }

  // 連絡先
  private toReceptionContact(bean: IReceptionContactBean): ReceptionContact {
    const model = new ReceptionContact();
    model.details = this.toReceptionContactDetails(bean.details);
    model.note = bean.note;

    return model;
  }

  private toReceptionContactDetails(details: IReceptionContactDetailBean[]): ReceptionContactDetail[] {
    return Enumerable.from(details).select(d => this.toReceptionContactDetail(d)).toArray();
  }

  private toReceptionContactDetail(detail: IReceptionContactDetailBean): ReceptionContactDetail {
    const contactDetail = new ReceptionContactDetail();
    contactDetail.isRequired = detail.isRequired;
    contactDetail.contacts = detail.contacts;
    contactDetail.contactName = detail.contactName;
    return contactDetail;
  }

  // 対応責任者
  private toReceptionManagers(beans: IReceptionManagerBean[]): ReceptionManager[] {
    return Enumerable.from(beans)
      .select(bean => this.toReceptionManager(bean))
      .toArray();
  }

  private toReceptionManager(bean: IReceptionManagerBean): ReceptionManager {
    return new ReceptionManager(bean.displayOrder, this.toUser(bean.staff));
  }

  // 証明期間
  private toReceptionProofPeriods(beans: IReceptionProofPeriodBean[], defaultCount: number): ReceptionProofPeriod[] {

    const array = Enumerable.from(beans)
      .select(bean => this.toReceptionProofPeriod(bean))
      .toArray();

    this.fillProofPeriods(array, defaultCount);

    return array;
  }

  private toReceptionProofPeriod(bean: IReceptionProofPeriodBean): ReceptionProofPeriod {
    if (!bean) { return new ReceptionProofPeriod(0); }

    const model = new ReceptionProofPeriod(bean.displayOrder);
    model.type = bean.type;
    model.period = new DateDuration(this._dateTranslator.fromDateString(bean.startDate), this._dateTranslator.fromDateString(bean.endDate));

    return model;
  }

  // 既定個数に満たない場合は空枠を追加
  private fillProofPeriods(array: ReceptionProofPeriod[], defaultCount: number): void {
    for (let i = array.length + 1; array.length < defaultCount; i++) {
      array.push(new ReceptionProofPeriod(i));
    }
}

// コメント
  public toReceptionCommentSet(bean: IReceptionCommentSetBean): ReceptionCommentSet {
    if (!bean) { return new ReceptionCommentSet(); }

    const model = new ReceptionCommentSet();
    model.receptionDetailId = bean.receptionDetailId;
    model.isReadOnly = !!bean.isReadOnly;
    model.comments = this.toReceptionComments(bean.comments);
    model.restrictComments = this.toReceptionComments(bean.restrictComments);
    model.nextMessages = this.toReceptionComments(bean.nextMessages);
    model.backMessages = this.toReceptionComments(bean.backMessages);
    model.contactedComments = this.toReceptionComments(bean.contactedComments);
    model.delComments = this.toReceptionComments(bean.delComments);
    model.conflictComments = this.toReceptionComments(bean.conflictComments);

    return model;
  }

  private toReceptionComments(beans: IReceptionCommentBean[]): ReceptionComment[] {
    if (!beans) { return []; }

    return Enumerable.from(beans)
      .select(bean => this.toReceptionComment(bean))
      .toArray();
  }

  public toReceptionComment(bean: IReceptionCommentBean): ReceptionComment {
    if (!bean) { return new ReceptionComment(); }

    const model = new ReceptionComment();
    model.id = bean.id;
    model.archiveId = bean.archiveId; // 登録競合チェックで使用
    model.type = bean.type;
    model.content = bean.content;
    model.deleted = bean.deleted;
    model.createDateTime = this._dateTranslator.fromDateTimeString(bean.createDateTime);
    model.createUser = this.toUser(bean.createUser);
    model.isEditable = bean.isEditable;
    model.isDeletable = bean.isDeletable;
    model.updateDateTime = this._dateTranslator.fromDateTimeString(bean.updateDateTime);

    return model;
  }

  public toWorkflowRouteEditors(beans: IWorkflowRouteEditorBean[]): WorkflowRouteEditorModel[] {
    if (!beans) { return null; }
    return Enumerable.from(beans).select(b => this.toWorkflowRouteEditor(b)).toArray();
  }

  // WFルート設定
  public toWorkflowRouteEditor(bean: IWorkflowRouteEditorBean): WorkflowRouteEditorModel {
    if (!bean) { return null; }

    const model = new WorkflowRouteEditorModel();
    model.progressCondition = bean.progressCondition;
    model.phases = this.toWorkflowPhaseEditors(bean.phases);
    model.isPaper = bean.isPaper;
    model.id = bean.id;
    model.name = bean.name;

    return model;
  }

  // WFフェーズ設定
  private toWorkflowPhaseEditors(beans: IWorkflowPhaseEditorBean[]): WorkflowPhaseEditorModel[] {
    return Enumerable.from(beans)
      .select(bean => this.toWorkflowPhaseEditor(bean))
      .toArray();
  }

  private toWorkflowPhaseEditor(bean: IWorkflowPhaseEditorBean): WorkflowPhaseEditorModel {
    if (!bean) { return null; }

    const model = new WorkflowPhaseEditorModel();
    model.correspondStatus = this.toWorkflowStatusName(bean.correspondStatus);
    model.limitDays = bean.limitDays;
    model.limitDate = this._dateTranslator.fromDateString(bean.limitDate);
    model.isEnable = bean.isEnable;
    model.phaseStatus = bean.phaseStatus;

    return model;
  }

  // ---------------- 権限系（ボタン） -----------------------

  private toReceptionPermissions(bean: IReceptionPermissionsBean): ReceptionPermissions {
    if (!bean) { return null; }

    const model = new ReceptionPermissions();
    model.edit = this.toButtonInfo(bean.edit, "受付編集");
    model.delete = this.toButtonInfo(bean.delete, "受付削除");
    model.copy = this.toButtonInfo(bean.copy, "受付コピー");
    model.quote = this.toButtonInfo(bean.quote, "受付引用");

    return model;
  }

  private toReceptionEditorPermissions(bean: IReceptionEditorPermissionsBean): ReceptionEditorPermissions {
    if (!bean) { return null; }

    const model = new ReceptionEditorPermissions();
    model.registerTemporary = this.toButtonInfo(bean.registerTemporary, "一時保存");
    model.register = this.toButtonInfo(bean.register, "登録");
    model.registerAndRequestSheet = this.toButtonInfo(bean.registerAndRequestSheet, "登録（依頼書作成）");
    model.requestSheetCreate = this.toButtonInfo(bean.requestSheetCreate, "依頼書作成");

    return model;
  }

  private toReceptionDetailPermissions(bean: IReceptionDetailPermissionsBean): ReceptionDetailPermissions { 
    if (!bean) { return null; }

    const model = new ReceptionDetailPermissions();
    model.edit = this.toButtonInfo(bean.edit, "依頼編集");
    model.delete = this.toButtonInfo(bean.delete, "依頼削除");
    model.suspend = this.toButtonInfo(bean.suspend, "一時退避へ");
    model.resume = this.toButtonInfo(bean.resume, "現在の進捗に戻す");
    model.cancel = this.toButtonInfo(bean.cancel, "依頼取消");
    model.return = this.toButtonInfo(bean.return, "依頼復活");
    model.copy = this.toButtonInfo(bean.copy, "依頼コピー");
    model.quote = this.toButtonInfo(bean.quote, "依頼引用");
    model.requestAnother = this.toButtonInfo(bean.requestAnother, "他医依頼");

    model.documentShow = this.toButtonInfo(bean.documentShow, "文書参照");
    model.documentCreate = this.toButtonInfo(bean.documentCreate, "文書作成");
    model.documentQuote = this.toButtonInfo(bean.documentQuote, "文書作成（引用）");
    model.documentEdit = this.toButtonInfo(bean.documentEdit, "文書編集");
    model.approve = this.toButtonInfo(bean.approve, "確認");
    model.sendBack = this.toButtonInfo(bean.sendBack, "差戻し");

    const sendBackButtonName = (bean.sendBack && bean.sendBack.buttonName) ? bean.sendBack.buttonName : "差戻し";
    const editButtonName = (bean.documentEdit && bean.documentEdit.buttonName) ? bean.documentEdit.buttonName : "文書編集";
    const sendBackAndEditButtonName = sendBackButtonName + "＆" + editButtonName;
    model.sendBackAndEdit = this.toButtonInfo(bean.sendBackAndEdit, sendBackAndEditButtonName);

    model.publish = this.toButtonInfo(bean.publish, "発行");
    model.finish = this.toButtonInfo(bean.finish, "終了");
    model.cancelFinish = this.toButtonInfo(bean.cancelFinish, "終了取消");
    model.finishWithoutCost = this.toButtonInfo(bean.finishWithoutCost, "コストなし終了");
    model.publishReceipt = this.toButtonInfo(bean.publishReceipt, "受領書発行");

    model.nextMessage = this.toButtonInfo(bean.nextMessage, "伝達事項");
    model.backMessage = this.toButtonInfo(bean.backMessage, "差戻しコメント");
    model.deleteMessage = this.toButtonInfo(bean.deleteMessage, "削除理由");

    model.requestSheetShow = this.toButtonInfo(bean.requestSheetShow, "依頼書参照");
    model.requestSheetCreate = this.toButtonInfo(bean.requestSheetCreate, "依頼書作成");
    model.requestSheetEdit = this.toButtonInfo(bean.requestSheetEdit, "依頼書編集");
    model.requestSheetDelete = this.toButtonInfo(bean.requestSheetDelete, "依頼書削除");

    return model;
  }

  private toWorkflowDocumentPermissions(bean: IWorkflowDocumentPermissionsBean): WorkflowDocumentPermissions {
    if (!bean) { return null; }

    const model = new WorkflowDocumentPermissions();
    model.register = this.toButtonInfo(bean.register, "登録");
    model.registerTemporary = this.toButtonInfo(bean.registerTemporary, "一時保存");
    model.print = this.toButtonInfo(bean.print, "印刷");
    model.registerAndPrint = this.toButtonInfo(bean.registerAndPrint, "登録＆印刷");
    model.edit = this.toButtonInfo(bean.edit, "編集");
    model.approve = this.toButtonInfo(bean.approve, "確認");
    model.sendBack = this.toButtonInfo(bean.sendBack, "差戻し");
    model.publish = this.toButtonInfo(bean.publish, "発行");
    model.writeAndPublish = this.toButtonInfo(bean.writeAndPublish, "登録・発行");
    model.finish = this.toButtonInfo(bean.finish, "終了");
    model.cancelFinish = this.toButtonInfo(bean.cancelFinish, "終了取消");
    model.finishWithoutCost = this.toButtonInfo(bean.finishWithoutCost, "コストなし終了");
    model.nextMessageShow = this.toButtonInfo(bean.nextMessageShow, "伝達事項");
    model.nextMessageRegister = this.toButtonInfo(bean.nextMessageRegister, "伝達事項");
    model.backMessageShow = this.toButtonInfo(bean.backMessageShow, "差戻しコメント");
    model.backMessageRegister = this.toButtonInfo(bean.backMessageRegister, "差戻しコメント");

    return model;
  }

  // ---------------- 文書 ---------------------------------

  public toWorkflowDocumentSaveResult(bean: IWorkflowDocumentSaveResultBean): WorkflowDocumentSaveResult {
    if (!bean) { return null; }

    const model = new WorkflowDocumentSaveResult();
    this.setReceptionSaveResultBase(bean, model);
    model.savedDocumentType = bean.savedDocumentType;
    model.conflictType = bean.conflictType ? bean.conflictType : 0;
    model.conflictOperation = this.toWorkflowOperation(bean.conflictOperation);
    model.confirmButtonName = bean.confirmButtonName;
    model.isFirstNormalRegister = bean.isFirstNormalRegister;
    model.workflowAutoRegisterResult = this.toWorkflowAutoRegisterResult(bean.workflowAutoRegisterResult);

    return model;
  }

  private toWorkflowAutoRegisterResult(bean: IWorkflowAutoRegisterResultBean): WorkflowAutoRegisterResult {
    if (!bean) { return null; }

    const model = new WorkflowAutoRegisterResult();
    model.success = bean.success;
    model.message = bean.message;
    model.reason = bean.reason;

    return model;
  }

  public createWorkflowDocument(document: Document, bean: IWorkflowDocumentSupplementBean): WorkflowDocument {
    if (!document || !bean) {
      return null;
    }
    const result = new WorkflowDocument();
    result.targetId = bean.targetId;
    result.document = document;
    if (bean.department) {
      result.document.attributes.department = this._documentTranslator.toDepartment(bean.department);
    }
    result.permissions = this.toWorkflowDocumentPermissions(bean.permissions);
    result.status = this.toWorkflowStatusName(bean.status);
    result.isDisableValidation = bean.isDisableValidation;
    result.nextMessageCommentId = bean.nextMessageCommentId; // 登録競合チェックで使用
    result.nextMessageArchiveId = bean.nextMessageArchiveId; // 登録競合チェックで使用
    result.nextMessage = bean.nextMessage;
    result.backMessage = bean.backMessage;
    result.workflowHisInformation = this.toWorkflowHisInformation(bean.workflowHisInformation);
    result.lastWorkflowHistoryId = bean.lastWorkflowHistoryId;
    result.createTokens = this.toCreateTokens(bean.createTokens);
    if (bean.newDocumentDateTime) {
      result.document.attributes.createdDate = this.toDate(bean.newDocumentDateTime);
    }
    return result;
  }

  public toWorkflowDocumentInfo(bean: IWorkflowDocumentInfoBean): WorkflowDocumentInfo {
    if (!bean) { return null; }

    const model = new WorkflowDocumentInfo();
    model.targetId = bean.targetId;
    model.type = bean.type;
    model.documentId = bean.documentId;

    return model;
  }

  public toWorkflowDocumentViewer(bean: IWorkflowDocumentViewBean): WorkflowDocumentViewer {
    if (!bean) { return null; }

    const model = new WorkflowDocumentViewer();
    model.targetId = bean.targetId;
    model.documentId = bean.documentId;
    model.permissions = this.toWorkflowDocumentPermissions(bean.permissions);
    model.status = this.toWorkflowStatusName(bean.status);

    model.labelSetting = this.toReceptionDispInfo(bean.labelSetting);
    model.commentSetting = this.toReceptionCommentDispInfo(bean.commentSetting);
    model.commentSet = this.toReceptionCommentSet(bean.commentSet);

    model.lastWorkflowHistoryId = bean.lastWorkflowHistoryId;
    model.isTargetDeleted = bean.isTargetDeleted;

    return model;
  }

  public toWorkflowHisInformation(bean: IWorkflowHisInformationBean): WorkflowHisInformation {
    if (!bean) { return null; }

    const model = new WorkflowHisInformation();
    model.items = this.toHisInformationItems(bean.items);
    model.hospitalizationItems = this.toHisInformationItems(bean.hospitalizationItems);
    model.diseaseItems = this.toHisInformationItems(bean.diseaseItems);
    model.editPhaseTypes = bean.editPhaseTypes;
    model.managerUserCodes = bean.managerUserCodes;
    model.hospitalizationItemSets = this.toHospitalizationItemSets(bean.hospitalizationItemSets);
    model.isCommitPhase = bean.isCommitPhase;

    return model;
  }

  // 管理外文書の記載済みWF文書情報
  public toUnmanagedDocumentSummaryInformations(models: IUnmanagedDocumentSummaryInformationBean[]): UnmanagedDocumentSummaryInformation[] {
    return Enumerable.from(models).select(model => this.toUnmanagedDocumentSummaryInformation(model)).toArray();
  }

  private toHospitalizationItemSets(beans: IHisInformationSetBean[]): HisInformationItemSet[] {
    if (!beans) { return null; }
    return beans.map(bean => this.toHospitalizationItemSet(bean));
  }

  private toHospitalizationItemSet(bean: IHisInformationSetBean): HisInformationItemSet {
    const items = this.toHisInformationItems(bean.items);
    return new HisInformationItemSet(items);
  }

  private toCreateTokens(bean: IDocumentTokenInformationBean): DocumentTokenInformation {
    if (!bean) { return null; }
    return new DocumentTokenInformation(
      bean.token,
      bean.tokenLifeSpan,
      this.toCurrentEditors(bean.editors),
    );
  }

  public toCurrentEditors(beans: ICurrentEditorBean[]): CurrentEditors[] {
    if (!beans) { return []; }
    return Enumerable.from(beans).select(b => this.toCurrentEditor(b)).toArray();
  }

  private toCurrentEditor(bean: ICurrentEditorBean): CurrentEditors {
    return new CurrentEditors(
      bean.userName,
      bean.terminalName,
      this.toDate(bean.editStartDateTime),
      bean.token,
    );
  }

  private toDate(dateTimeAsString: string): Date {
    if (!dateTimeAsString) { return new Date(); }
    const parsedDateTime = moment(dateTimeAsString, "YYYYMMDDHHmmss").toDate();
    return !parsedDateTime
      ? new Date()
      : parsedDateTime;
  }

  private toReceptionListCardStyleDispInfo(bean: IReceptionListCardStyleDispInfoBean): ReceptionListCardStyleDispInfo {
    if (!bean) { return null; }
    return {
      shouldShowDeliveryKind: bean.shouldShowDeliveryKind,
    };
  }
}
